import {
  onboardingStepToUpdateUserOnboardingStep,
  toUpdateUserOnboardingStep,
} from './stepUtils';

import { useOnboarding, useUser } from '@cobliteam/hooks';

import { DateUtils } from '@cobliteam/base-components';

import type { OnboardingStep } from '../store/slices/onboardingSteps';

const useUpdateOnboarding = () => {
  const { useGetUserData } = useUser();
  const { data: userData } = useGetUserData();
  const onboarding = userData?.userPreferences?.onboarding;

  const {
    useSaveUserOnboarding,
    useCreateUserOnboarding,
    useResetUserOnboarding,
  } = useOnboarding();

  const { mutate: saveOnboarding } = useSaveUserOnboarding();
  const { mutate: createUserOnboarding } = useCreateUserOnboarding();
  const { mutate: resetUserOnboarding } = useResetUserOnboarding();

  const createOnboarding = () => {
    createUserOnboarding();
  };

  const resetOnboarding = () => {
    resetUserOnboarding();
  };

  const updateOnboarding = (startedAt?: number, dismissedAt?: number) => {
    saveOnboarding({
      started_at: startedAt ?? onboarding?.startedAt,
      dismissed_at: dismissedAt ?? onboarding?.dismissedAt,
      steps: onboarding?.steps.map(toUpdateUserOnboardingStep) ?? [],
    });
  };

  const updateOnboardingSteps = (step: OnboardingStep) => {
    const newSteps = onboarding?.steps.map((onboardingStep) => {
      if (step.stepName === onboardingStep.stepInfo.step) {
        return onboardingStepToUpdateUserOnboardingStep(step);
      }

      return toUpdateUserOnboardingStep(onboardingStep);
    });

    saveOnboarding({
      started_at: onboarding?.startedAt,
      dismissed_at: onboarding?.dismissedAt,
      steps: newSteps ?? [],
    });
  };

  const dismissOnboarding = () => {
    updateOnboarding(undefined, DateUtils.getDateInMillis());
  };

  const startOnboardingIfNotStarted = () => {
    if (!onboarding?.startedAt) {
      updateOnboarding(DateUtils.getDateInMillis(), undefined);
    }
  };

  const startStep = (step?: OnboardingStep) => {
    if (step && !step.endedAt && !step.startedAt) {
      updateOnboardingSteps({
        ...step,
        startedAt: DateUtils.getDateInMillis(),
      });
    }
  };

  const endStep = (step?: OnboardingStep) => {
    if (step && !step.endedAt) {
      updateOnboardingSteps({
        ...step,
        endedAt: DateUtils.getDateInMillis(),
      });
    }
  };

  const skipStep = (step?: OnboardingStep) => {
    if (step && !step.endedAt) {
      updateOnboardingSteps({
        ...step,
        skippedAt: DateUtils.getDateInMillis(),
      });
    }
  };

  const unskipStep = (step?: OnboardingStep) => {
    if (step && !step.endedAt) {
      updateOnboardingSteps({
        ...step,
        skippedAt: undefined,
      });
    }
  };

  return {
    startStep,
    endStep,
    skipStep,
    unskipStep,
    startOnboardingIfNotStarted,
    dismissOnboarding,
    updateOnboarding,
    createOnboarding,
    resetOnboarding,
  };
};

export default useUpdateOnboarding;
